import { useEffect } from 'react'
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from 'lucide-react'
import { useToastStore, type Toast } from '../store/toastStore'
import clsx from 'clsx'

const icons = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
}

// Bílá karta s barevným proužkem a ikonou — čitelné na jakémkoli pozadí, kontrast textu AA.
const accents = {
  success: { bar: 'bg-emerald-500', icon: 'text-emerald-600' },
  error: { bar: 'bg-red-600', icon: 'text-red-600' },
  info: { bar: 'bg-sky-500', icon: 'text-sky-600' },
  warning: { bar: 'bg-amber-500', icon: 'text-amber-600' },
}

function ToastItem({ toast }: { toast: Toast }) {
  const { removeToast } = useToastStore()
  const Icon = icons[toast.type]
  const accent = accents[toast.type]

  useEffect(() => {
    // Chyby zůstávají déle — člověk si je musí stihnout přečíst.
    const timer = setTimeout(() => removeToast(toast.id), toast.type === 'error' ? 7000 : 4000)
    return () => clearTimeout(timer)
  }, [toast.id, toast.type, removeToast])

  return (
    <div
      role={toast.type === 'error' ? 'alert' : 'status'}
      className="pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-xl border border-slate-200 bg-white py-3 pl-4 pr-2 shadow-soft-lg animate-fade-in sm:w-96"
    >
      <span className={clsx('absolute inset-y-0 left-0 w-1', accent.bar)} aria-hidden />
      <Icon size={20} className={clsx('mt-0.5 shrink-0', accent.icon)} />
      <span className="flex-1 pt-0.5 text-sm text-ink-900">{toast.message}</span>
      <button
        type="button"
        onClick={() => removeToast(toast.id)}
        aria-label="Zavřít oznámení"
        className="-my-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-ink-500 hover:bg-slate-100 hover:text-ink-900"
      >
        <X size={16} />
      </button>
    </div>
  )
}

/**
 * Oznámení (toasty). API beze změny: `useToastStore().addToast(type, message)`.
 * Mobil: nahoře přes celou šířku (pod výřezem displeje), desktop: vpravo nahoře.
 */
export default function Toasts() {
  const { toasts } = useToastStore()

  if (toasts.length === 0) return null

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-0 z-[1100] flex flex-col items-center gap-2 px-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:inset-x-auto sm:right-4 sm:items-end sm:px-0"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  )
}
